/**
 * SignatureList - Display signatures on a collection item
 * MDM-UNIFIED-001 Phase 7: Signature Tracking
 */
import React, { useState, useEffect } from 'react';
import {
  PenTool, Award, Shield, Calendar, MapPin, Camera,
  Edit2, Trash2, Plus, ChevronDown, ChevronRight,
  CheckCircle, AlertCircle, Star, Loader2
} from 'lucide-react';
import { getSignatures, deleteSignature } from '../../services/vaultApi';
import SignatureForm from './SignatureForm';

// Witness type badges
const WITNESS_BADGES = {
  cgc_ss: { label: 'CGC SS', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  cbcs_verified: { label: 'CBCS Verified', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  self: { label: 'Self-Witnessed', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
};

// Format date
const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

function SignatureCard({ signature, onEdit, onDelete, isDeleting }) {
  const witnessInfo = WITNESS_BADGES[signature.witness_type];

  return (
    <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Signer Name */}
          <div className="flex items-center gap-2 mb-1">
            <PenTool className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <span className="font-medium text-white truncate">
              {signature.signer_name}
            </span>
            {signature.creator_name && (
              <span className="text-xs text-zinc-500">
                ({signature.creator_name})
              </span>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {/* Verification badge */}
            {witnessInfo ? (
              <span className={`text-xs px-2 py-0.5 rounded border ${witnessInfo.color}`}>
                {witnessInfo.label}
              </span>
            ) : signature.is_witnessed ? (
              <span className="text-xs px-2 py-0.5 rounded border bg-green-500/20 text-green-400 border-green-500/30">
                Witnessed
              </span>
            ) : signature.is_verified ? (
              <span className="text-xs px-2 py-0.5 rounded border bg-blue-500/20 text-blue-400 border-blue-500/30">
                Verified
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded border bg-zinc-600/20 text-zinc-400 border-zinc-600/30">
                Unverified
              </span>
            )}

            {/* Location badge */}
            {signature.signature_location && (
              <span className="text-xs px-2 py-0.5 rounded border bg-zinc-600/20 text-zinc-400 border-zinc-600/30">
                {signature.signature_location}
              </span>
            )}

            {/* Premium badge */}
            {signature.estimated_premium_pct && (
              <span className="text-xs px-2 py-0.5 rounded border bg-orange-500/20 text-orange-400 border-orange-500/30">
                +{signature.estimated_premium_pct}%
              </span>
            )}
          </div>

          {/* Details */}
          <div className="text-xs text-zinc-400 space-y-0.5">
            {signature.obtained_at && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3" />
                <span>{signature.obtained_at}</span>
                {signature.obtained_date && (
                  <span className="text-zinc-500">({formatDate(signature.obtained_date)})</span>
                )}
              </div>
            )}
            {signature.signature_date && !signature.obtained_date && (
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                <span>Signed {formatDate(signature.signature_date)}</span>
              </div>
            )}
            {signature.signature_notes && (
              <p className="text-zinc-500 truncate">{signature.signature_notes}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {signature.proof_photo_url && (
            <a
              href={signature.proof_photo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 hover:bg-zinc-700 rounded transition-colors"
              title="View proof photo"
            >
              <Camera className="w-4 h-4 text-zinc-400" />
            </a>
          )}
          <button
            onClick={() => onEdit(signature)}
            className="p-1.5 hover:bg-zinc-700 rounded transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4 text-zinc-400" />
          </button>
          <button
            onClick={() => onDelete(signature.id)}
            disabled={isDeleting}
            className="p-1.5 hover:bg-zinc-700 rounded transition-colors disabled:opacity-50"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SignatureList({ collectionItemId, onSignaturesChange }) {
  const [signatures, setSignatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSignature, setEditingSignature] = useState(null);

  useEffect(() => {
    if (collectionItemId) {
      loadSignatures();
    }
  }, [collectionItemId]);

  const loadSignatures = async () => {
    setIsLoading(true);
    try {
      const result = await getSignatures({ collection_item_id: collectionItemId });
      setSignatures(result.items || []);
      setStats({
        total: result.total,
        cgc_ss_count: result.cgc_ss_count,
        cbcs_verified_count: result.cbcs_verified_count,
        witnessed_count: result.witnessed_count,
        unverified_count: result.unverified_count,
      });
    } catch (error) {
      console.error('Failed to load signatures:', error);
    }
    setIsLoading(false);
  };

  const handleDelete = async (signatureId) => {
    if (!confirm('Delete this signature?')) return;

    setIsDeleting(true);
    try {
      await deleteSignature(signatureId);
      await loadSignatures();
      onSignaturesChange?.();
    } catch (error) {
      console.error('Failed to delete signature:', error);
    }
    setIsDeleting(false);
  };

  const handleEdit = (signature) => {
    setEditingSignature(signature);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSignature(null);
  };

  const handleFormSave = async () => {
    await loadSignatures();
    onSignaturesChange?.();
    handleFormClose();
  };

  return (
    <div className="border-t border-zinc-800 pt-4 mt-4">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <div className="flex items-center gap-2">
          <PenTool className="w-4 h-4 text-purple-400" />
          <span className="font-medium text-white">Signatures</span>
          {stats && stats.total > 0 && (
            <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">
              {stats.total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingSignature(null);
              setShowForm(true);
            }}
            className="p-1 hover:bg-zinc-700 rounded transition-colors"
            title="Add signature"
          >
            <Plus className="w-4 h-4 text-zinc-400" />
          </button>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-zinc-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-3">
          {/* Stats */}
          {stats && stats.total > 0 && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              {stats.cgc_ss_count > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Award className="w-3 h-3" />
                    <span>CGC SS</span>
                  </div>
                  <p className="text-yellow-300 font-medium">{stats.cgc_ss_count}</p>
                </div>
              )}
              {stats.cbcs_verified_count > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2">
                  <div className="flex items-center gap-1 text-blue-400">
                    <Shield className="w-3 h-3" />
                    <span>CBCS Verified</span>
                  </div>
                  <p className="text-blue-300 font-medium">{stats.cbcs_verified_count}</p>
                </div>
              )}
              {stats.witnessed_count > 0 && stats.cgc_ss_count === 0 && stats.cbcs_verified_count === 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
                  <div className="flex items-center gap-1 text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    <span>Witnessed</span>
                  </div>
                  <p className="text-green-300 font-medium">{stats.witnessed_count}</p>
                </div>
              )}
              {stats.unverified_count > 0 && (
                <div className="bg-zinc-500/10 border border-zinc-500/30 rounded p-2">
                  <div className="flex items-center gap-1 text-zinc-400">
                    <AlertCircle className="w-3 h-3" />
                    <span>Unverified</span>
                  </div>
                  <p className="text-zinc-300 font-medium">{stats.unverified_count}</p>
                </div>
              )}
            </div>
          )}

          {/* Signatures List */}
          {isLoading ? (
            <div className="text-center py-4">
              <Loader2 className="w-5 h-5 text-orange-500 animate-spin mx-auto" />
            </div>
          ) : signatures.length === 0 ? (
            <div className="text-center py-4">
              <PenTool className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No signatures recorded</p>
              <button
                onClick={() => {
                  setEditingSignature(null);
                  setShowForm(true);
                }}
                className="mt-2 text-xs text-orange-400 hover:text-orange-300 transition-colors"
              >
                Add your first signature
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {signatures.map((signature) => (
                <SignatureCard
                  key={signature.id}
                  signature={signature}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeleting={isDeleting}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Signature Form Modal */}
      {showForm && (
        <SignatureForm
          collectionItemId={collectionItemId}
          signature={editingSignature}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
}
